import {
  ageLabel, genderShort, doseNotation,
  MEDICINE_TIMING_LABEL, TEST_URGENCY_LABEL, VISIT_TYPE_LABEL,
  type ClinicSettings, type Doctor, type Letterhead, type Patient,
  type Prescription, type Visit, type Vitals,
} from '../api'
import { medicineLabel } from '../clinical'

interface Props {
  clinic: ClinicSettings | null
  doctor: Doctor
  patient: Patient
  visit: Visit
  vitals: Vitals[]
  prescription: Prescription
  letterhead: Letterhead | null
}

function dmy(iso: string | null): string {
  if (!iso) return '—'
  // A full instant is read in the clinic's local day; a bare date is used as is.
  const day = iso.length > 10 ? new Date(iso).toLocaleDateString('en-CA') : iso
  const [y, m, d] = day.split('-')
  return d && m && y ? `${d}/${m}/${y}` : iso
}

/**
 * The A4 prescription (blueprint 10). This is the only part of the system
 * anyone outside the clinic ever sees, so it is laid out to the millimetre.
 *
 * Two decisions carry the layout:
 *
 * The sheet is a table. `thead` and `tfoot` are the one mechanism browsers
 * reliably repeat across printed pages, so a prescription that runs to a
 * second page carries the patient strip and the footer onto it without a
 * pagination library.
 *
 * Page margins are padding, not `@page` margins. `@page` cannot read the
 * per-doctor millimetre settings, so the page box is set to zero and the
 * configured margins are applied to the sheet - which also lets pre-printed
 * stationery reserve exactly the right blank space at the top.
 */
export default function PrescriptionPrint({
  clinic, doctor, patient, visit, vitals, prescription, letterhead,
}: Props) {
  const mode = letterhead?.mode ?? 'full'
  const preprinted = mode === 'preprinted'

  const marginTop = letterhead?.marginTopMm ?? (preprinted ? '45' : '12')
  const marginBottom = letterhead?.marginBottomMm ?? (preprinted ? '30' : '12')
  const marginLeft = letterhead?.marginLeftMm ?? '15'
  const marginRight = letterhead?.marginRightMm ?? '15'

  const showVitals = letterhead?.showVitalsStrip ?? true
  const showAllergy = letterhead?.showAllergyBanner ?? true

  const latest = vitals[0] ?? null
  const vitalBits: string[] = []
  if (latest?.weightKg) vitalBits.push(`Wt ${latest.weightKg} kg`)
  if (latest?.bpSystolic && latest?.bpDiastolic) {
    vitalBits.push(`BP ${latest.bpSystolic}/${latest.bpDiastolic} mmHg`)
  }
  if (latest?.pulseBpm) vitalBits.push(`Pulse ${latest.pulseBpm}/min`)
  if (latest?.temperatureF) vitalBits.push(`Temp ${latest.temperatureF} °F`)
  if (latest?.spo2Pct) vitalBits.push(`SpO₂ ${latest.spo2Pct}%`)

  const address = [clinic?.addressLine1, clinic?.addressLine2, clinic?.city, clinic?.pincode]
    .filter(Boolean).join(', ')
  const phones = [clinic?.phone, clinic?.altPhone].filter(Boolean).join(' · ')

  return (
    <div className="rx-print">
      {/*
        Scoped to this component rather than the global stylesheet: the A5
        receipt on the reception screen sets its own page box, and the two
        must not fight. Only one of them is ever mounted at a time.
      */}
      <style>{'@media print { @page { size: A4 portrait; margin: 0; } }'}</style>

      <table
        className="rx-sheet"
        style={{
          paddingTop: `${marginTop}mm`,
          paddingBottom: `${marginBottom}mm`,
          paddingLeft: `${marginLeft}mm`,
          paddingRight: `${marginRight}mm`,
        }}
      >
        <thead>
          <tr>
            <td>
              {/* Zone 1 - suppressed on stationery that already carries it. */}
              {!preprinted && (
                <div className="rx-head">
                  <div className="rx-clinic">
                    <div className="rx-clinic-name">{clinic?.clinicName ?? 'Clinic'}</div>
                    {address && <div className="rx-line">{address}</div>}
                    {phones && <div className="rx-line">{phones}</div>}
                    {clinic?.opdTimings && <div className="rx-line">OPD: {clinic.opdTimings}</div>}
                  </div>
                  <div className="rx-doctor">
                    <div className="rx-doctor-name">{doctor.fullName}</div>
                    {doctor.qualifications && <div className="rx-line">{doctor.qualifications}</div>}
                    {doctor.specialization && <div className="rx-line">{doctor.specialization}</div>}
                    {/*
                      Legally required: a prescription without the Medical
                      Council registration number is not a valid prescription.
                    */}
                    <div className="rx-line rx-reg">Reg. No. {doctor.registrationNo ?? '—'}</div>
                  </div>
                </div>
              )}
              {!preprinted && <div className="rx-rule" />}

              {/* Zone 2 - the patient strip, repeated on every page. */}
              <div className="rx-patient">
                <span><strong>{patient.fullName}</strong></span>
                <span>{ageLabel(patient)} / {genderShort(patient.gender)}</span>
                <span>{patient.patientCode}</span>
                <span>{dmy(visit.visitDate)}</span>
                <span>Visit #{visit.visitNo} · {VISIT_TYPE_LABEL[visit.visitType]}</span>
                {patient.mobile && <span>{patient.mobile}</span>}
              </div>

              {showAllergy && patient.allergies && (
                <div className="rx-allergy">Allergies: {patient.allergies}</div>
              )}

              {/* Zone 3 */}
              {showVitals && vitalBits.length > 0 && (
                <div className="rx-vitals">{vitalBits.join('   ·   ')}</div>
              )}
            </td>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td className="rx-body">
              {/* Zone 4 */}
              {prescription.chiefComplaints && (
                <Section label="Complaints">{prescription.chiefComplaints}</Section>
              )}
              {prescription.historyPresentIllness && (
                <Section label="History">{prescription.historyPresentIllness}</Section>
              )}
              {prescription.examinationFindings && (
                <Section label="Findings">{prescription.examinationFindings}</Section>
              )}
              {prescription.provisionalDiagnosis && (
                <Section label="Diagnosis">
                  {prescription.provisionalDiagnosis}
                  {prescription.icd10Code ? ` (${prescription.icd10Code})` : ''}
                </Section>
              )}

              {prescription.medicines.length > 0 && (
                <div className="rx-meds">
                  <div className="rx-symbol">℞</div>
                  <table className="rx-med-table">
                    <thead>
                      <tr>
                        <th className="c-sr">#</th>
                        <th>Medicine</th>
                        <th className="c-dose">Dosage</th>
                        <th className="c-timing">Timing</th>
                        <th className="c-dur">Duration</th>
                        <th className="c-qty">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prescription.medicines.map((m, i) => (
                        <tr key={m.id ?? i}>
                          <td className="c-sr">{i + 1}</td>
                          <td>
                            <div className="rx-med-name">
                              {medicineLabel(m.drugName, m.strength)}
                            </div>
                            {m.instructions && <div className="rx-med-note">{m.instructions}</div>}
                          </td>
                          <td className="c-dose">
                            {doseNotation(m.doseMorning, m.doseAfternoon, m.doseNight)}
                          </td>
                          <td className="c-timing">
                            {m.timing ? MEDICINE_TIMING_LABEL[m.timing] : ''}
                          </td>
                          <td className="c-dur">{m.durationDays ? `${m.durationDays} days` : ''}</td>
                          <td className="c-qty">{m.quantity ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {prescription.tests.length > 0 && (
                <div className="rx-tests">
                  <div className="rx-section-label">Investigations advised</div>
                  <ol>
                    {prescription.tests.map((t, i) => (
                      <li key={t.id ?? i}>
                        {t.testName}
                        {t.urgency !== 'routine' && (
                          <span className="rx-urgent"> — {TEST_URGENCY_LABEL[t.urgency]}</span>
                        )}
                        {t.instructions && <div className="rx-med-note">{t.instructions}</div>}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {prescription.advice && <Section label="Advice">{prescription.advice}</Section>}

              {prescription.followUpDate && (
                <div className="rx-followup">
                  Review on <strong>{dmy(prescription.followUpDate)}</strong>
                  {prescription.followUpDays ? ` (after ${prescription.followUpDays} days)` : ''}
                </div>
              )}

              {prescription.addenda.length > 0 && (
                <div className="rx-addenda">
                  <div className="rx-section-label">Addendum</div>
                  {/* The author is named on the paper, not only in the audit
                      log. This block prints above one doctor's signature and
                      registration number, so a note somebody else wrote would
                      otherwise read as theirs. */}
                  {prescription.addenda.map((a) => (
                    <div key={a.id} className="rx-addendum">
                      <span className="rx-addendum-date">{dmy(a.createdAt)}</span> {a.note}
                      {a.createdByName && (
                        <span className="rx-addendum-by"> — {a.createdByName}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td>
              {/* Zone 5 */}
              <div className="rx-foot">
                <div className="rx-sign">
                  <div className="rx-sign-line" />
                  <div>{doctor.fullName}</div>
                  <div className="rx-line">Reg. No. {doctor.registrationNo ?? '—'}</div>
                </div>
                <div className="rx-foot-note">
                  {letterhead?.footerNote && <div>{letterhead.footerNote}</div>}
                  <div>Generated {new Date().toLocaleString()}</div>
                  {phones && <div>{phones}</div>}
                </div>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rx-section">
      <div className="rx-section-label">{label}</div>
      <div className="rx-section-body">{children}</div>
    </div>
  )
}
